import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ChartForm from "./ChartForm";
import ChartWheel from "./ChartWheel";
import ChartReadout from "./ChartReadout";

// The form's state lives in the URL and the chart comes from the server, for
// the same reason the Tree's lenses do: a chart is a thing you send someone.
// Nothing is computed here.

const PARAM_FOR = {
  date: "date", time: "time", place: "place",
  latitude: "latitude", longitude: "longitude",
  timezone: "timezone", houseSystem: "houses", orbSet: "orbs", occurrence: "occurrence"
};

function toQuery(form) {
  const params = new URLSearchParams();
  Object.entries(PARAM_FOR).forEach(([key, param]) => {
    if (form[key] !== null && form[key] !== undefined && form[key] !== "") {
      params.set(param, form[key]);
    }
  });
  return params.toString();
}

// An ambiguous local time has no single answer, so the page asks instead of
// choosing. This is the one piece of chart UI that has to exist and is usually
// missing.
function AmbiguityPrompt({ chart, onResolve }) {
  return (
    <div className="ambiguity" data-testid="ambiguity-prompt">
      <h2 className="ambiguity__heading">That time happened twice</h2>
      <p className="ambiguity__reason">{chart.reason}</p>
      <ul className="ambiguity__options">
        {chart.candidates.map((candidate) => (
          <li key={candidate.occurrence}>
            <button
              type="button"
              className="ambiguity__option"
              onClick={() => onResolve(candidate.occurrence)}
              data-testid={`resolve-${candidate.occurrence}`}
            >
              <strong>{candidate.abbreviation}</strong>
              <span>UTC{candidate.offsetHours >= 0 ? "+" : ""}{candidate.offsetHours}</span>
              <span>{candidate.dst ? "summer time" : "standard time"}</span>
              <span className="ambiguity__utc">{candidate.utc}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Swap the server-rendered readings for the ones belonging to the chart just
// fetched. React does not own this node — it is outside the island — so it is
// replaced directly, which is both the simplest correct thing and the reason
// the readings can stay out of the payload.
function replaceReadings(parsed) {
  const next = parsed.querySelector("#chart-readings");
  const live = document.querySelector("#chart-readings");
  if (!next || !live) return;
  live.innerHTML = next.innerHTML;
  live.removeAttribute("aria-busy");
}

export default function Astrology({ data }) {
  const [form, setForm] = useState(data.form);
  const [chart, setChart] = useState(data.chart);
  const [selected, setSelected] = useState(null);
  const [pending, setPending] = useState(false);
  const first = useRef(true);

  // Changing a field re-asks the server. Debounced, because typing a latitude
  // is six keystrokes and none of the intermediate values is a place.
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    const query = toQuery(form);
    const timer = setTimeout(() => {
      setPending(true);
      document.querySelector("#chart-readings")?.setAttribute("aria-busy", "true");
      window.history.replaceState({}, "", `${window.location.pathname}?${query}`);
      fetch(`${window.location.pathname}?${query}`, {
        headers: { Accept: "text/html" }
      })
        .then((response) => response.text())
        .then((html) => {
          const parsed = new DOMParser().parseFromString(html, "text/html");
          const node = parsed.querySelector("#astrology");
          if (node) setChart(JSON.parse(node.dataset.astrology).chart);
          // The delineations are rendered on the SERVER, below the wheel, and
          // are deliberately not in the payload — an opinion inside the data
          // of an exact calculation would blur the only line this section has.
          // They still have to move when the wheel moves: without this the
          // page showed a new chart above last chart's readings, which is a
          // worse lie than showing none. The reply already carries the whole
          // document, so the section is taken from it rather than refetched.
          replaceReadings(parsed);
        })
        .finally(() => setPending(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [form]);

  const resolveAmbiguity = useCallback(
    (occurrence) => setForm((current) => ({ ...current, occurrence })),
    []
  );

  // Changing anything but the occurrence clears a stale resolution: the answer
  // to "which 01:30" is meaningless once the date moves.
  const handleFormChange = useCallback((next) => {
    setForm((current) => (
      next.date !== current.date || next.time !== current.time || next.timezone !== current.timezone
        ? { ...next, occurrence: null }
        : next
    ));
  }, []);

  const deferred = useMemo(() => data.deferredHouseSystems || [], [data.deferredHouseSystems]);

  return (
    <main className={pending ? "astro astro--pending" : "astro"} data-testid="astrology">
      <header className="astro__masthead">
        <h1 className="astro__heading">Natal chart</h1>
        <p className="astro__lead">
          Computed from birth data. The chart keeps its positions, houses and
          aspects together at the top of the reading; the astronomical working
          remains below so the result can be checked against another tool.
        </p>
      </header>

      <div className="astro__layout">
        <div className="astro__controls">
          <ChartForm
            form={form}
            timezones={data.timezones}
            houseSystems={data.houseSystems}
            deferred={deferred}
            orbSets={data.orbSets}
            onChange={handleFormChange}
          />
        </div>

        <div className="astro__chart">
          {chart.resolved ? (
            <ChartWheel chart={chart} selected={selected} onSelect={setSelected} />
          ) : chart.candidates && chart.candidates.length > 0 ? (
            <AmbiguityPrompt chart={chart} onResolve={resolveAmbiguity} />
          ) : (
            <p className="astro__problem" data-testid="chart-problem">{chart.reason}</p>
          )}
        </div>

        <div className="astro__readout">
          {chart.resolved && <ChartReadout chart={chart} />}
        </div>
      </div>

      {/* CC BY 4.0 requires attribution. It is a licence condition, so it is
          rendered rather than buried in a README. */}
      <footer className="astro__credits">
        City data from{" "}
        <a href="https://www.geonames.org/" rel="noopener noreferrer" target="_blank">GeoNames</a>,
        used under{" "}
        <a href="https://creativecommons.org/licenses/by/4.0/" rel="noopener noreferrer" target="_blank">
          CC BY 4.0
        </a>. Time zone rules from the IANA database via TZInfo. Planetary and
        quadrant-house calculations use{" "}
        <a href="https://www.astro.com/swisseph/" rel="noopener noreferrer" target="_blank">
          Swiss Ephemeris
        </a>{" "}
        under AGPL-3.0-or-later.{" "}
        <a href="https://github.com/jkirch/tree-of-life" rel="noopener noreferrer" target="_blank">
          Source code
        </a>.
      </footer>
    </main>
  );
}
